/**
 * The Tax Profile page: everything the tax team knows about the client's legal entity, where each
 * value came from, and the documents the values were read from.
 *
 * Expects no props. The client it shows is the one the staff user is viewing
 * (impersonatingAccountId from UserContext), and it says "You must select a client" when nobody is
 * being viewed.
 *
 * Under the page heading a toggle switches between two views of the same answer. The page opens on
 * Profile, ProfileView: the values only, as cards and read-only tables, with the tax year switch.
 * Review is the rest of this description; the Review tab carries the count of values waiting for a
 * decision. While Profile is shown the Review view's rows stay mounted, hidden, so nothing chosen
 * there is lost.
 *
 * Renders the page heading, where the legal entity's name beside the title is a dropdown of the
 * client's entities when there is more than one, and, in the Review view, the Add document button
 * (not drawn while the head reads "No legal entity yet");
 * under them the hint sentence with how many values wait for a decision, and on the right the button that accepts every
 * value where all sources agree; and then two columns (stacked below Tailwind's xl width):
 * SectionIndex on the left with DocumentActivity inside it, and on the right one ProfileGroup per
 * catalog section followed by OwnersTable, OfficersTable, SubsidiariesTable and DocumentsTable. ReviewModal,
 * DocumentPreviewModal and AddDocumentModal are rendered under all of that; DocumentPreviewModal
 * comes after the tables so that it draws over an owner's or an officer's window when a document
 * name inside that window is clicked.
 *
 * The rows of the owners, officers, subsidiaries and state registrations tables are reviewed one
 * row at a time with the row's own Finish review button (ListTable.tsx), so the accept-all button
 * leaves them out and its label says so while one of them waits.
 *
 * Every write a row offers — accepting one of the values found, keeping the approved value, typing
 * a value, putting back what an edit replaced — goes through useProfileFieldWrites, so the page
 * only says which value was acted on and how. A document's summary screen (ReviewModal) writes
 * nothing; its "Go to field" closes it, turns both filters off, switches to the line's legal entity
 * and, for a per-year value, to its tax year, then scrolls to the value's row or list row and
 * marks it with HIGHLIGHT_ROW_CLASS for ROW_HIGHLIGHT_MS.
 *
 * The documents table, the left column's Activity lines and the Documents count of the section
 * index show only the documents filed under the legal entity shown, plus every document filed under
 * no legal entity, which is shown on every entity so it is never out of sight
 * (documentIsOnEntity). ReviewModal and the preview of a value's source document are handed the
 * whole list, because a value's source can be a document of another entity and ReviewModal matches
 * a newer document by entity itself.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'react-daisyui';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TAX_PROFILE_FIELDS,
  TAX_PROFILE_FIELD_GROUPS,
  taxProfileFieldByKey,
  taxProfileListColumnByKey,
} from '@shared/constants/taxProfileFields';
import type { TaxProfileFieldDefinition } from '@shared/constants/taxProfileFields';
import type { OcrDocumentType } from '@shared/constants/taxProfile';
import type {
  AnswerTaxDocumentEntityRequest,
  TaxDocumentExtractionStatus,
  TaxDocumentListItem,
  TaxDocumentReviewLine,
  TaxProfileFieldValue,
} from '@shared/types/taxProfile';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import PageContainer from '@/components/PageContainer';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import { Spinner } from '@/components/Spinner';
import { IUserContext, useUserContext } from '@/context/UserContext';
import useTaxProfile, { documentIsBeingRead } from '@/hooks/useTaxProfile';
import { alertErrorMessage, alertWarningMessageNode } from '@/utils/alerts';
import type { ApiRequestError } from '@/utils/api';
import { fieldIsShown, fieldNeedsReview } from '@/pages/TaxProfile/fieldState';
import useProfileFieldWrites, {
  targetFromField,
} from '@/pages/TaxProfile/useProfileFieldWrites';
import {
  DOCUMENT_COULD_NOT_BE_OPENED,
  ENTITY_ANSWER_FAILED,
  LIST_ROW_NOUNS,
  PROFILE_HINT,
  REFRESH_FAILED_SENTENCE,
  UNSAVED_CHOICES_SENTENCE,
  VIEW_WORDS,
  acceptAgreeingLabel,
  acceptAgreeingQuestion,
  documentTitle,
  waitingForDecisionSentence,
} from '@/pages/TaxProfile/wording';
import AddDocumentModal, {
  AddDocumentChoice,
} from '@/pages/TaxProfile/components/AddDocumentModal';
import BulkReviewButton from '@/pages/TaxProfile/components/BulkReviewButton';
import DocumentActivity from '@/pages/TaxProfile/components/DocumentActivity';
import DocumentPreviewModal from '@/pages/TaxProfile/components/DocumentPreviewModal';
import { fieldRowElementId } from '@/pages/TaxProfile/components/FieldRow';
import { listRowElementId } from '@/pages/TaxProfile/components/ListTable';
import type { ListRowNoun } from '@/pages/TaxProfile/wording';
import DocumentsTable, {
  DOCUMENTS_SECTION_ID,
  documentRowId,
  scrollToId,
} from '@/pages/TaxProfile/components/DocumentsTable';
import OfficersTable, {
  OFFICERS_SECTION_ID,
} from '@/pages/TaxProfile/components/OfficersTable';
import OwnersTable, {
  OWNERS_SECTION_ID,
} from '@/pages/TaxProfile/components/OwnersTable';
import ProfileGroup, {
  ProfileFieldEntry,
  groupAnchorId,
  offeredTaxYears,
  singleValueEntries,
} from '@/pages/TaxProfile/components/ProfileGroup';
import ProfileView from '@/pages/TaxProfile/components/ProfileView';
import ReviewModal from '@/pages/TaxProfile/components/ReviewModal';
import SectionIndex, {
  CURRENT_SECTION_CLASS,
  SECTION_FRAME_CLASS,
  SectionIndexEntry,
} from '@/pages/TaxProfile/components/SectionIndex';
import SubsidiariesTable, {
  SUBSIDIARIES_SECTION_ID,
} from '@/pages/TaxProfile/components/SubsidiariesTable';
import { rowIsWaiting } from '@/pages/TaxProfile/listRows';
import type { FieldDecision } from '@/pages/TaxProfile/components/ValuePicker';
import { BRAND_ACTIVE_TAB_CLASS } from '@/pages/TaxProfile/brand';

/**
 * How long a row carries HIGHLIGHT_ROW_CLASS after the page sent the reader to it: a document's row
 * from a line in the left column, and a value's row or a list row from "Go to field" on a
 * document's summary.
 */
const ROW_HIGHLIGHT_MS = 2000;

/**
 * True for a document the page shows while the legal entity entityId is shown: one filed under that
 * entity, and one filed under no legal entity, which is shown on every entity so that it is never
 * out of sight. Every document is shown while the page has no entity to show.
 */
const documentIsOnEntity = (
  document: Pick<TaxDocumentListItem, 'legal_entity_id'>,
  entityId: number | null
): boolean =>
  entityId === null ||
  document.legal_entity_id === null ||
  document.legal_entity_id === entityId;

export function TaxProfileContent({
  clientId,
  isSuperAdmin = false,
}: {
  clientId: number;
  isSuperAdmin?: boolean;
}) {
  const {
    getProfile,
    getDocuments,
    getDocumentLink,
    refreshProfile,
    refreshDocuments,
    readDocument,
    answerDocumentEntity,
    updateDocument,
  } = useTaxProfile();
  const { auth, impersonate }: IUserContext = useUserContext();

  /**
   * Which view the toggle at the top of the page has on: 'profile', the values only (ProfileView),
   * or 'review', everything else on the page. The page opens on 'profile' every time.
   */
  const [view, setView] = useState<'profile' | 'review'>('profile');
  const [entityId, setEntityId] = useState<number | null>(null);
  const [taxYear, setTaxYear] = useState<number | null>(null);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [busyDocumentId, setBusyDocumentId] = useState<number | null>(null);
  const [addInFlight, setAddInFlight] = useState<{
    count: number;
    choice: AddDocumentChoice;
  } | null>(null);
  const [finishedDocumentIds, setFinishedDocumentIds] = useState<number[]>([]);
  const [highlightedDocumentId, setHighlightedDocumentId] = useState<
    number | null
  >(null);
  /** The document DocumentPreviewModal shows the file of; null while closed. */
  const [previewDocumentId, setPreviewDocumentId] = useState<number | null>(
    null
  );
  /** The document ReviewModal is open on, from the documents table; null while closed. */
  const [reviewingDocumentId, setReviewingDocumentId] = useState<number | null>(
    null
  );
  /**
   * The element "Go to field" asked for, until it is on the page: a switch of entity or tax year
   * draws it only once the new answer arrives. Null when nothing is asked for.
   */
  const [pendingGoToId, setPendingGoToId] = useState<string | null>(null);
  /** The value's row or list row "Go to field" sent the page to, which is marked; else null. */
  const [highlightedElementId, setHighlightedElementId] = useState<
    string | null
  >(null);
  /** The anchorId of the section SectionIndex marks as the one in view; null until it reports. */
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
  /**
   * How many rows of each list table (by the table's noun) have choices Confirm review has not
   * saved yet, as ListTable reports them through onUnsavedChoicesChange.
   */
  const [unsavedByTable, setUnsavedByTable] = useState<
    Partial<Record<ListRowNoun, number>>
  >({});
  const hasUnsavedChoices = Object.values(unsavedByTable).some(
    (count) => (count ?? 0) > 0
  );
  const reportUnsavedChoices = useCallback(
    (noun: ListRowNoun, count: number) =>
      setUnsavedByTable((kept) =>
        (kept[noun] ?? 0) === count ? kept : { ...kept, [noun]: count }
      ),
    []
  );
  /** The extraction_status each document had in the documents response before the latest one. */
  const statusesBefore = useRef<Map<
    number,
    TaxDocumentExtractionStatus
  > | null>(null);

  /*
   * profileLoading is true while the answer for a newly picked entity or tax year is on its way.
   * getProfile asks SWR to keep the previous answer in `profile` for that time, so the page stays
   * drawn (and keeps its scroll position) instead of falling back to the loading line below.
   */
  const {
    data: profile,
    error: profileError,
    isLoading: profileLoading,
  } = getProfile(clientId, entityId, taxYear);
  const { data: documentsResponse, error: documentsError } =
    getDocuments(clientId);
  // The legal entity the page shows and every write names: the one picked in the title's dropdown,
  // else the one the profile answer is for (the client's first). Worked out here, before the page's
  // early returns, because the hook below has to be called before them.
  const shownEntityId = entityId ?? profile?.legalEntity?.id ?? null;
  const writes = useProfileFieldWrites(clientId, shownEntityId);

  const fieldsByKey = useMemo(() => {
    const byKey: Record<string, TaxProfileFieldValue> = {};
    (profile?.fields ?? []).forEach((field) => {
      byKey[field.key] = field;
    });
    return byKey;
  }, [profile?.fields]);

  const definitionsByGroup = useMemo(
    () =>
      TAX_PROFILE_FIELD_GROUPS.map((group) => ({
        group,
        definitions: TAX_PROFILE_FIELDS.filter(
          (definition) => definition.group === group.key
        ),
      })).filter((entry) => entry.definitions.length > 0),
    []
  );

  /**
   * The section the year switch is drawn in: the first one whose catalog entries include a value
   * kept per tax year. The switch changes the year of the whole page, so the page draws one; the
   * other sections that hold such values name the chosen year in their heading instead.
   */
  const yearSwitchGroupKey =
    definitionsByGroup.find(({ definitions }) =>
      definitions.some((definition) => definition.yearly)
    )?.group.key ?? null;

  /** Every row the two filters leave on screen, across the sections, with its catalog entry. */
  const shownEntries: ProfileFieldEntry[] = definitionsByGroup.flatMap(
    ({ definitions }) =>
      definitions
        .map((definition) => ({
          definition,
          field: fieldsByKey[definition.key],
        }))
        .filter(({ field }) =>
          fieldIsShown(field, { needsReviewOnly, hideEmpty })
        )
  );
  const acceptableShown = singleValueEntries(shownEntries);

  /** Every document of the client, whatever legal entity it is filed under. */
  const documents = documentsResponse?.documents ?? [];
  /** The documents the table and the left column show for the legal entity shown. */
  const entityDocuments = documents.filter((document) =>
    documentIsOnEntity(document, shownEntityId)
  );

  /** How many rows of each list table wait for a person, for the section index. */
  const waitingOwners = (profile?.owners ?? []).filter(rowIsWaiting).length;
  const waitingOfficers = (profile?.officers ?? []).filter(rowIsWaiting).length;
  const waitingSubsidiariesAndStates = [
    ...(profile?.subsidiaries ?? []),
    ...(profile?.stateTaxIds ?? []),
  ].filter(rowIsWaiting).length;
  const tableRowsWaiting =
    waitingOwners + waitingOfficers + waitingSubsidiariesAndStates > 0;

  /**
   * Notices the documents whose reading has just finished and fetches the profile again once for
   * them, so the values a reading added and the section counts appear without a click.
   *
   * The documents list is fetched again every five seconds while a reading is outstanding
   * (documentsRefreshInterval in useTaxProfile.ts). Each time it comes back, the documents that
   * were queued or processing in the response before and are not any more have just been read; they
   * get a line in the left column. A response in which no document changed state fetches nothing.
   */
  useEffect(() => {
    if (!documentsResponse) return;
    const statusesNow = new Map(
      documentsResponse.documents.map((document) => [
        document.id,
        document.extraction_status,
      ])
    );
    const previous = statusesBefore.current;
    statusesBefore.current = statusesNow;
    if (!previous) return;
    const justRead = documentsResponse.documents.filter((document) => {
      const before = previous.get(document.id);
      return (
        (before === 'queued' || before === 'processing') &&
        !documentIsBeingRead(document)
      );
    });
    if (justRead.length === 0) return;
    setFinishedDocumentIds((kept) => [
      ...kept.filter((id) => !justRead.some((document) => document.id === id)),
      ...justRead.map((document) => document.id),
    ]);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    refreshProfile(clientId, entityId, taxYear);
    // refreshProfile comes from useTaxProfile(), which returns a new object on every render, so it
    // cannot be a dependency. The effect has to run on a new documents response and nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsResponse, clientId, entityId, taxYear]);

  /** Takes HIGHLIGHT_ROW_CLASS off a document's row ROW_HIGHLIGHT_MS after the left column sent the page. */
  useEffect(() => {
    if (highlightedDocumentId === null) return undefined;
    const timer = setTimeout(
      () => setHighlightedDocumentId(null),
      ROW_HIGHLIGHT_MS
    );
    return () => clearTimeout(timer);
  }, [highlightedDocumentId]);

  /**
   * Scrolls to the element "Go to field" asked for and marks it, once it is on the page. It runs
   * again on each profile answer, which is when the rows of a newly chosen entity or tax year are
   * drawn.
   */
  useEffect(() => {
    if (pendingGoToId === null) return;
    if (!window.document.getElementById(pendingGoToId)) return;
    scrollToId(pendingGoToId);
    setHighlightedElementId(pendingGoToId);
    setPendingGoToId(null);
  }, [pendingGoToId, profile]);

  /** Takes HIGHLIGHT_ROW_CLASS off a row "Go to field" sent the page to, ROW_HIGHLIGHT_MS later. */
  useEffect(() => {
    if (highlightedElementId === null) return undefined;
    const timer = setTimeout(
      () => setHighlightedElementId(null),
      ROW_HIGHLIGHT_MS
    );
    return () => clearTimeout(timer);
  }, [highlightedElementId]);

  /**
   * While a row has choices Confirm review has not saved, closing the tab, reloading or following a
   * link out of the dashboard asks the browser's own "Leave site?" question first. The listener is
   * there only while such choices exist. A move to another page inside the dashboard does not go
   * through the browser, so it asks nothing.
   */
  useEffect(() => {
    if (!hasUnsavedChoices) return undefined;
    const ask = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers show the question only when returnValue is set; none of them shows the text.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = UNSAVED_CHOICES_SENTENCE;
      return UNSAVED_CHOICES_SENTENCE;
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [hasUnsavedChoices]);

  /**
   * Switches the tax year or the legal entity the page shows. While a row has choices Confirm
   * review has not saved, a warning toast says so; the switch still happens. The choices stay with
   * their rows and tax year (ListTable's decisionKey) and come back on switching back, unless a
   * table the new entity has no rows for went away with them.
   */
  const warnOfUnsavedChoices = () => {
    if (hasUnsavedChoices) alertWarningMessageNode(UNSAVED_CHOICES_SENTENCE);
  };
  const switchTaxYear = (year: number) => {
    warnOfUnsavedChoices();
    setTaxYear(year);
  };
  const switchEntity = (id: number | null) => {
    warnOfUnsavedChoices();
    setEntityId(id);
  };

  const readingCount = entityDocuments.filter(documentIsBeingRead).length;

  /**
   * The documents the left column's finished lines name, in the order their readings finished, of
   * those the legal entity shown lists. A document whose reading was asked for again is left out,
   * because the line above it, which says how many documents are being read, already counts it.
   */
  const finishedDocuments = finishedDocumentIds
    .map((id) => entityDocuments.find((document) => document.id === id))
    .filter(
      (document): document is TaxDocumentListItem =>
        document !== undefined && !documentIsBeingRead(document)
    );

  const showDocumentRow = (documentId: number) => {
    setFinishedDocumentIds((kept) => kept.filter((id) => id !== documentId));
    setHighlightedDocumentId(documentId);
    scrollToId(documentRowId(documentId));
  };

  const indexEntries: SectionIndexEntry[] = [
    ...definitionsByGroup.map(({ group, definitions }) => ({
      anchorId: groupAnchorId(group.key),
      label: group.label,
      count: definitions.filter((definition) => {
        const field = fieldsByKey[definition.key];
        return field ? fieldNeedsReview(field) : false;
      }).length,
      countsReview: true,
    })),
    {
      anchorId: OWNERS_SECTION_ID,
      label: 'Owners',
      count: waitingOwners,
      countsReview: true,
    },
    {
      anchorId: OFFICERS_SECTION_ID,
      label: 'Officers',
      count: waitingOfficers,
      countsReview: true,
    },
    {
      anchorId: SUBSIDIARIES_SECTION_ID,
      label: 'Subsidiaries and states',
      count: waitingSubsidiariesAndStates,
      countsReview: true,
    },
    {
      anchorId: DOCUMENTS_SECTION_ID,
      label: 'Documents',
      count: entityDocuments.length,
      countsReview: false,
    },
  ];

  /**
   * Every value and every table row that waits for a person, whatever the filters show: the sum of
   * the yellow badges of the section index, so the line at the top and the badges always agree.
   */
  const waitingCount = indexEntries
    .filter((entry) => entry.countsReview)
    .reduce((sum, entry) => sum + entry.count, 0);

  const refreshBoth = async () => {
    await Promise.all([
      refreshProfile(clientId, entityId, taxYear),
      refreshDocuments(clientId),
    ]);
  };

  /**
   * Asks the api to read one document and fetches the documents list again.
   *
   * Expects the document's id and, only for a row whose "Add document type" button opened
   * ChooseDocumentTypeModal, the type picked there. The read route takes that type as its optional
   * docType, and requestTaxDocumentExtraction writes it onto the row and reads the file as that
   * type in the one request.
   */
  const handleReadDocument = async (
    documentId: number,
    docType?: OcrDocumentType
  ) => {
    setBusyDocumentId(documentId);
    try {
      await readDocument(clientId, documentId, docType ? { docType } : {});
      await refreshDocuments(clientId);
    } catch (error) {
      alertErrorMessage(
        (error as Error).message || 'The document could not be read'
      );
    } finally {
      setBusyDocumentId(null);
    }
  };

  /**
   * Sends a person's answer about a held document and waits for the documents list and the profile
   * to be fetched again (answerDocumentEntity does both, whatever the api answered).
   *
   * After a Move there the api answers with the document filed under the entity it was moved to,
   * and the page switches to that entity and marks and scrolls to the document's row once it is
   * drawn, the way "Go to field" waits for its row (pendingGoToId): the person made the move, so the
   * page takes them to where the document went.
   *
   * A 409 means somebody already answered for this document; the refreshed row shows what they
   * decided, so nothing more is said. Any other refusal shows the api's own sentence in a toast,
   * such as the 502 of a Keep here whose values could not be recorded, where the row stays held and
   * keeps its buttons.
   */
  const handleAnswerEntity = async (
    documentId: number,
    answer: AnswerTaxDocumentEntityRequest
  ) => {
    setBusyDocumentId(documentId);
    try {
      const { document } = await answerDocumentEntity(
        clientId,
        documentId,
        answer
      );
      if (
        answer.answer === 'move' &&
        document.legal_entity_id !== null &&
        document.legal_entity_id !== shownEntityId
      ) {
        switchEntity(document.legal_entity_id);
        setHighlightedDocumentId(document.id);
        setPendingGoToId(documentRowId(document.id));
      }
    } catch (error) {
      if ((error as ApiRequestError)?.status !== 409) {
        alertErrorMessage((error as Error).message || ENTITY_ANSWER_FAILED);
      }
    } finally {
      setBusyDocumentId(null);
    }
  };

  /**
   * "Open that client" on a document whose EIN belongs to another client: switches the client being
   * viewed with UserContext's impersonate, which is what the Investors menu does
   * (components/Nav/InvestorsMenu.tsx) and what the top bar's client selector does by hand
   * (components/Nav/ImpersonationMessage.tsx). The page stays on the Tax Profile, now of that client.
   */
  const openOtherClient = impersonate
    ? (client: { id: number; client_name: string }) =>
        impersonate(client.id, client.client_name)
    : undefined;

  /**
   * Whether the person may view that client, by the rule the top bar's client selector lists
   * clients by (ImpersonationMessage.tsx): a super admin may view every client, anybody else the
   * clients in authorizedClientIds.
   */
  const canOpenClient = (otherClientId: number): boolean =>
    auth?.isSuperAdmin === true ||
    (auth?.authorizedClientIds ?? []).includes(otherClientId);

  const handleUpdateYear = async (
    documentId: number,
    docYear: number | null
  ) => {
    setBusyDocumentId(documentId);
    try {
      await updateDocument(clientId, documentId, { docYear });
      await refreshDocuments(clientId);
    } catch (error) {
      alertErrorMessage(
        (error as Error).message || 'The tax year could not be saved'
      );
    } finally {
      setBusyDocumentId(null);
    }
  };

  // The whole page is replaced only while there is nothing to show yet. SWR keeps the last answer
  // in `data` and sets `error` when a later fetch fails (a poll of the documents list, a refresh
  // after a write, a refocus), so replacing the page then threw away unsaved choices, an open
  // summary and ticked files. With data on screen the page stays and says so in one line
  // (refreshFailed below), which goes once a later fetch succeeds and SWR clears the error.
  if ((profileError && !profile) || (documentsError && !documentsResponse)) {
    return (
      <ErrorAlert
        isSuperAdmin={isSuperAdmin}
        error={(profileError || documentsError) as unknown}
        context='TaxProfile'
      />
    );
  }

  if (!profile) {
    return (
      <div className='p-4 flex items-center gap-2'>
        <Spinner />
        Loading the tax profile
      </div>
    );
  }

  /**
   * True while the rows on screen belong to the entity or tax year picked before the one now asked
   * for. Every write button waits for the new answer, because a write would name the new tax year
   * against a row of the old one.
   */
  const showsPreviousAnswer = profileLoading === true;
  /** True while the page shows an earlier answer because the latest fetch of either read failed. */
  const refreshFailed = !!profileError || !!documentsError;
  const rowsBusy = writes.isWriting || showsPreviousAnswer;
  /**
   * SECTION_FRAME_CLASS on every section, with CURRENT_SECTION_CLASS added on the section
   * SectionIndex marks as in view.
   */
  const sectionGlow = (anchorId: string) =>
    anchorId === currentSectionId
      ? `${SECTION_FRAME_CLASS} ${CURRENT_SECTION_CLASS}`
      : SECTION_FRAME_CLASS;

  const legalEntities = profile.legalEntities ?? [];
  /**
   * True while the page head reads "No legal entity yet": no dropdown of entities and no entity
   * shown. The api refuses to add a document to a client with no legal entity (one is created from
   * the onboarding form's answers, never from the client's name), so neither Add document button
   * is drawn then.
   */
  const noLegalEntityYet = legalEntities.length <= 1 && !profile.legalEntity;
  /** The current tax year and the one before it, with the years the entity has values for. */
  const yearsOnOffer = offeredTaxYears(
    profile.taxYears ?? [],
    new Date().getFullYear()
  );
  const shownTaxYear = taxYear ?? profile.taxYear ?? yearsOnOffer[0];

  /** Accepts, in one request, each value's one waiting value (targetFromField's acceptRowId). */
  const acceptAll = (entries: ProfileFieldEntry[]) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    writes.markManyReviewed(
      entries.map(({ field, definition }) =>
        targetFromField(field, definition, shownTaxYear)
      )
    );
  };

  /**
   * Writes what a person decided about one value: accepting a waiting value sends the review route
   * with that value's first source as rowId, keeping the approved value sends it with no rowId, and
   * a typed value is written through the edit route (PUT).
   */
  const decide = (
    field: TaxProfileFieldValue,
    definition: TaxProfileFieldDefinition,
    decision: FieldDecision
  ) => {
    const { acceptRowId, ...target } = targetFromField(
      field,
      definition,
      shownTaxYear
    );
    if (decision.kind === 'type') {
      writes.editValue(target, decision.value);
      return;
    }
    writes.markReviewed(
      decision.kind === 'accept'
        ? { ...target, acceptRowId: decision.rowId }
        : target
    );
  };

  /** Opens a document's file in a new tab, from the address the api's link route gives. */
  const openDocumentInNewTab = async (documentId: number) => {
    try {
      const { url } = await getDocumentLink(clientId, documentId);
      window.open(url, '_blank', 'noopener');
    } catch {
      alertErrorMessage(DOCUMENT_COULD_NOT_BE_OPENED);
    }
  };

  /**
   * Opens a document's file: in DocumentPreviewModal on the page when the documents response has
   * the file's onedrive_item_id and the client's teamId, which are what the preview route needs;
   * otherwise in a new tab, as before (a document missing from the documents list, or a client with
   * no team, where the link route answers 404 and the toast says the file could not be opened).
   */
  const openSourceDocument = (documentId: number) => {
    const document = documents.find((entry) => entry.id === documentId);
    if (document?.onedrive_item_id && documentsResponse?.teamId) {
      setPreviewDocumentId(documentId);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    openDocumentInNewTab(documentId);
  };
  const previewDocument =
    documents.find((entry) => entry.id === previewDocumentId) ?? null;

  /**
   * "Go to field" on a line of a document's summary: closes the summary, turns both filters off so
   * the row is drawn, switches to the line's legal entity and, for a per-year value, to its tax
   * year, and asks for the value's row (a catalog field) or its list row to be scrolled to.
   * ReviewModal offers the link only on a line with a key and, for a list value, a row.
   */
  const goToLine = (line: TaxDocumentReviewLine) => {
    if (line.key === null) return;
    let elementId: string;
    if (line.list === null) {
      elementId = fieldRowElementId(line.key);
    } else if (line.recordId !== null) {
      elementId = listRowElementId(LIST_ROW_NOUNS[line.list], line.recordId);
    } else {
      return;
    }
    setReviewingDocumentId(null);
    setNeedsReviewOnly(false);
    setHideEmpty(false);
    const switchesEntity =
      line.legalEntityId !== null && line.legalEntityId !== shownEntityId;
    const perYear =
      taxProfileFieldByKey(line.key)?.yearly ??
      taxProfileListColumnByKey(line.key)?.yearly ??
      false;
    const switchesYear =
      perYear && line.taxYear !== null && line.taxYear !== shownTaxYear;
    if (switchesEntity || switchesYear) warnOfUnsavedChoices();
    if (switchesEntity) setEntityId(line.legalEntityId);
    if (switchesYear) setTaxYear(line.taxYear);
    setPendingGoToId(elementId);
  };

  /**
   * The icon after a value that waits for a person, in the Profile view: switches to the Review
   * view and asks for the value's row or list row to be scrolled to and marked, as "Go to field"
   * does with pendingGoToId. The row is on the page whatever the filters say, because a value or a
   * list row that waits for a person passes both of them.
   */
  const reviewElement = (elementId: string) => {
    setView('review');
    setPendingGoToId(elementId);
  };

  /**
   * The Review view's rows stay mounted while the Profile view is shown, so the choices a list row
   * keeps until Confirm review (ListTable's state), an open value panel and the section index are
   * still there on switching back. They are hidden with the hidden attribute, which takes them out
   * of what a screen reader reads, and with Tailwind's hidden class, because their own flex class
   * would otherwise beat the attribute's display: none.
   */
  const reviewHiddenClass = view === 'review' ? '' : 'hidden';

  /** What the owners, officers, subsidiaries and state registrations tables share. */
  const listTableProps = {
    taxYear: shownTaxYear,
    needsReviewOnly,
    writes,
    busy: rowsBusy,
    onOpenSourceDocument: openSourceDocument,
    highlightedElementId,
    onUnsavedChoicesChange: reportUnsavedChoices,
  };

  return (
    <div className='p-4'>
      <div className='flex flex-wrap w-full justify-between items-center mb-2.5 gap-4'>
        <h1 className='text-lg font-bold uppercase flex flex-wrap items-center gap-x-3 whitespace-nowrap'>
          <FontAwesomeIcon
            icon={faFileInvoice}
            size='lg'
            className='text-white'
          />
          Tax Profile
          {legalEntities.length > 1 ? (
            <select
              className='select select-ghost select-sm normal-case font-normal text-sm opacity-70'
              aria-label='Legal entity'
              value={shownEntityId ?? ''}
              onChange={(event) => switchEntity(Number(event.target.value))}
            >
              {legalEntities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.entity_name}
                </option>
              ))}
            </select>
          ) : (
            <span className='normal-case font-normal text-sm opacity-70'>
              {profile.legalEntity?.entity_name ?? 'No legal entity yet'}
            </span>
          )}
        </h1>
        <div className='flex flex-wrap items-center gap-3'>
          {view === 'review' && !noLegalEntityYet && (
            <button
              type='button'
              className='btn btn-accent btn-sm'
              onClick={() => setIsAddOpen(true)}
            >
              Add document
            </button>
          )}
          {/* The toggle between the two views, at the right of the page head so it takes no row of its
            own, in the daisyUI tabs AddDocumentModal draws its two choices with. The Review tab
            carries waitingCount, the count of the line at the top of the Review view. */}
          <div
            className='tabs tabs-boxed w-fit'
            role='tablist'
            aria-label={VIEW_WORDS.label}
          >
            <button
              type='button'
              role='tab'
              aria-selected={view === 'profile'}
              className={`tab ${
                view === 'profile' ? `tab-active ${BRAND_ACTIVE_TAB_CLASS}` : ''
              }`}
              onClick={() => setView('profile')}
            >
              {VIEW_WORDS.profile}
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={view === 'review'}
              className={`tab gap-2 ${
                view === 'review' ? `tab-active ${BRAND_ACTIVE_TAB_CLASS}` : ''
              }`}
              onClick={() => setView('review')}
            >
              {VIEW_WORDS.review}
              <span className='badge badge-warning badge-sm'>
                {waitingCount}
              </span>
            </button>
          </div>
        </div>
      </div>

      {refreshFailed && (
        <p
          role='status'
          className='text-xs text-warning mb-2'
          data-testid='tax-profile-refresh-failed'
        >
          {REFRESH_FAILED_SENTENCE}
        </p>
      )}

      {view === 'profile' && (
        <ProfileView
          definitionsByGroup={definitionsByGroup}
          fieldsByKey={fieldsByKey}
          taxYears={yearsOnOffer}
          taxYear={shownTaxYear}
          onTaxYearChange={(year) => {
            if (year !== shownTaxYear) switchTaxYear(year);
          }}
          taxYearLoading={showsPreviousAnswer}
          owners={profile.owners}
          officers={profile.officers ?? []}
          subsidiaries={profile.subsidiaries}
          stateTaxIds={profile.stateTaxIds}
          waitingCount={waitingCount}
          onReview={() => setView('review')}
          onGoToElement={reviewElement}
        />
      )}

      {/* These rows are the full width of the page rather than the first thing in the right column,
          so that however the hint sentence wraps it does not push the right column's first table
          down past the top of SectionIndex beside it. The accept-all button sits at the right edge,
          above the Approval column of the sections. */}
      <div
        className={`flex flex-col gap-2 mb-3 ${reviewHiddenClass}`}
        hidden={view !== 'review'}
      >
        <div className='flex flex-wrap items-end justify-between gap-2'>
          <span className='text-xs opacity-70 max-w-3xl'>{PROFILE_HINT}</span>
          <span className='text-xs opacity-70 whitespace-nowrap'>
            {waitingForDecisionSentence(waitingCount)}
          </span>
        </div>
        <div className='flex justify-end'>
          <BulkReviewButton
            label={acceptAgreeingLabel(
              acceptableShown.length,
              tableRowsWaiting
            )}
            question={acceptAgreeingQuestion(acceptableShown.length)}
            count={acceptableShown.length}
            busy={rowsBusy}
            onConfirm={() => acceptAll(acceptableShown)}
          />
        </div>
      </div>

      {/* The section index sits beside the sections from xl (1280px) up. Below that the two stack,
          because beside it the sections' tables would be left too narrow to read. */}
      <div
        className={`flex flex-col xl:flex-row gap-4 items-start ${reviewHiddenClass}`}
        hidden={view !== 'review'}
      >
        <SectionIndex
          entries={indexEntries}
          needsReviewOnly={needsReviewOnly}
          onNeedsReviewOnlyChange={setNeedsReviewOnly}
          hideEmpty={hideEmpty}
          onHideEmptyChange={setHideEmpty}
          currentAnchorId={currentSectionId}
          onCurrentChange={setCurrentSectionId}
        >
          <DocumentActivity
            add={addInFlight}
            readingCount={readingCount}
            finished={finishedDocuments}
            onShowDocument={showDocumentRow}
            onDismiss={() => setFinishedDocumentIds([])}
          />
        </SectionIndex>

        <div className='flex-1 min-w-0 w-full'>
          {definitionsByGroup.map(({ group, definitions }) => (
            <ProfileGroup
              key={group.key}
              groupKey={group.key}
              label={group.label}
              definitions={definitions}
              fieldsByKey={fieldsByKey}
              needsReviewOnly={needsReviewOnly}
              hideEmpty={hideEmpty}
              taxYears={yearsOnOffer}
              taxYear={shownTaxYear}
              onTaxYearChange={(year) => {
                if (year !== shownTaxYear) switchTaxYear(year);
              }}
              showTaxYearSwitch={group.key === yearSwitchGroupKey}
              onDecide={decide}
              onOpenSourceDocument={openSourceDocument}
              noticesByKey={writes.notices}
              busy={rowsBusy}
              taxYearLoading={showsPreviousAnswer}
              highlightedElementId={highlightedElementId}
              className={sectionGlow(groupAnchorId(group.key))}
            />
          ))}

          <OwnersTable
            owners={profile.owners}
            {...listTableProps}
            className={sectionGlow(OWNERS_SECTION_ID)}
          />
          <OfficersTable
            officers={profile.officers ?? []}
            {...listTableProps}
            className={sectionGlow(OFFICERS_SECTION_ID)}
          />
          <SubsidiariesTable
            subsidiaries={profile.subsidiaries}
            stateTaxIds={profile.stateTaxIds}
            {...listTableProps}
            className={sectionGlow(SUBSIDIARIES_SECTION_ID)}
          />
          <DocumentsTable
            className={sectionGlow(DOCUMENTS_SECTION_ID)}
            documents={entityDocuments}
            busyDocumentId={busyDocumentId}
            highlightedDocumentId={highlightedDocumentId}
            onReadDocument={handleReadDocument}
            onReviewDocument={setReviewingDocumentId}
            onOpenDocument={openSourceDocument}
            onUpdateYear={handleUpdateYear}
            onAddDocument={
              noLegalEntityYet ? undefined : () => setIsAddOpen(true)
            }
            onAnswerEntity={handleAnswerEntity}
            onOpenClient={openOtherClient}
            canOpenClient={canOpenClient}
          />
        </div>
      </div>

      <ReviewModal
        clientId={clientId}
        documentId={reviewingDocumentId}
        onClose={() => setReviewingDocumentId(null)}
        onGoToLine={goToLine}
        documents={documents}
        onOpenDocumentSummary={setReviewingDocumentId}
        onOpenSourceDocument={openSourceDocument}
        writes={writes}
      />

      {previewDocument?.onedrive_item_id && documentsResponse?.teamId && (
        <DocumentPreviewModal
          itemId={previewDocument.onedrive_item_id}
          teamId={documentsResponse.teamId}
          title={documentTitle(previewDocument)}
          onOpenInNewTab={() => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            openDocumentInNewTab(previewDocument.id);
          }}
          onClose={() => setPreviewDocumentId(null)}
        />
      )}

      <AddDocumentModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        clientId={clientId}
        teamId={documentsResponse?.teamId ?? null}
        entityId={shownEntityId}
        onAddStarted={(count, choice) => setAddInFlight({ count, choice })}
        onAddFailed={() => setAddInFlight(null)}
        onAdded={async () => {
          setAddInFlight(null);
          await refreshBoth();
        }}
      />
    </div>
  );
}

export default function TaxProfile() {
  const { auth, companyName, impersonatingAccountId }: IUserContext =
    useUserContext();

  if (!impersonatingAccountId) {
    return (
      <PageContainer name='tax-profile'>
        <DashboardWrapper companyName={companyName || undefined}>
          <div className='max-w-sm mx-auto p-5'>
            <Alert icon={<ExclamationCircleIcon className='w-6 h-6' />}>
              You must select a client
            </Alert>
          </div>
        </DashboardWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer name='tax-profile'>
      <DashboardWrapper companyName={companyName || undefined}>
        {/* Keyed by the client, so that switching the client being viewed ("Open that client" on
            a held document, or the top bar's selector) starts from that client's first entity and
            the current tax year instead of asking for the previous client's entity. */}
        <TaxProfileContent
          key={impersonatingAccountId}
          clientId={impersonatingAccountId}
          isSuperAdmin={auth?.isSuperAdmin}
        />
      </DashboardWrapper>
    </PageContainer>
  );
}
